import type { BookingResponse, BookingStatus } from '@/shared/api';
import { formatBookingPrice } from '@/shared/lib/bookingPrice';
import { classifyChannel } from '../../calendar/calendarHelpers';
import { bookingStatusLabel, stayPhaseLabels } from '../components/bookingsFormat';
import { formatOccupancy } from '@/shared/lib/occupancy';
import { calcNights, channelName, isOta, stayDate } from './helpers';
import type { GuestState } from './useBookingWorkspace';

export type HeroTone = 'green' | 'amber' | 'red' | 'grey';

export interface HeroBadge {
  label: string;
  tone: HeroTone;
}

const STATUS_TONE: Record<BookingStatus, HeroTone> = {
  Pending: 'amber',
  Confirmed: 'green',
  CheckedIn: 'green',
  CheckedOut: 'grey',
  Cancelled: 'red',
  NoShow: 'red',
};

/** Phase mit Zeitpunkt, wo er feststeht: „Im Haus · bis Do 22.10.“. */
function phaseLabel(booking: BookingResponse): string | null {
  if (!booking.stayPhase) return null;
  if (booking.stayPhase === 'Arrival') return 'Anreise heute';
  if (booking.stayPhase === 'Departure') return 'Abreise heute';
  if (booking.stayPhase === 'InHouse') {
    return `Im Haus · bis ${stayDate(booking.actualDepartureDate ?? booking.checkOutDate, booking.objectToday)}`;
  }
  return stayPhaseLabels[booking.stayPhase];
}

/**
 * Status und Phase sind zwei Wahrheiten, sagen bei Storno und No-Show aber dasselbe Wort –
 * dann steht es einmal da („Storniert Storniert“ war Rauschen).
 */
export function heroBadges(booking: BookingResponse): HeroBadge[] {
  const status = {
    label: bookingStatusLabel(booking.status, booking.source),
    tone: STATUS_TONE[booking.status],
  };
  const phase = phaseLabel(booking);
  // Pending, Storno und No-Show: die Phase wiederholt nur den Status.
  if (
    !phase ||
    phase === status.label ||
    ['Pending', 'Cancelled', 'NoShow'].includes(booking.stayPhase ?? '')
  ) {
    return [status];
  }
  const tone: HeroTone =
    booking.stayPhase === 'DataQuality'
      ? 'red'
      : booking.stayPhase === 'Completed'
        ? 'grey'
        : 'green';
  return [status, { label: phase, tone }];
}

/**
 * Gastname; ohne Gastdaten (Eigentümer) „Buchung {Nummer}“ wie in der Liste, nie ein leeres
 * „Buchung“. Lädt das Gastprofil oder fehlt es, sagt der Titel das statt „Ohne Gastnamen“.
 */
export function heroTitle(
  booking: Pick<BookingResponse, 'bookingReference'>,
  guestName: string | null,
  canSeeGuests: boolean,
  guestState: GuestState,
): string {
  if (!canSeeGuests) return `Buchung ${booking.bookingReference}`;
  if (guestName) return guestName;
  if (guestState === 'loading') return 'Gast wird geladen';
  return guestState === 'error' ? 'Gast nicht geladen' : 'Ohne Gastnamen';
}

/** Eine Zeile mit allem, was die Buchung ausmacht; ohne Gastdaten steht die Nummer schon im Titel. */
export function heroMeta(
  booking: BookingResponse,
  propertyName: string | null,
  canSeeGuests: boolean,
): string {
  const nights = calcNights(booking.checkInDate, booking.checkOutDate);
  const arrival = booking.arrivalHour ? ` · Ankunft ca. ${booking.arrivalHour.slice(0, 5)}` : '';
  const parts = [
    canSeeGuests ? booking.bookingReference : null,
    `${stayDate(booking.checkInDate, booking.objectToday)} → ${stayDate(booking.checkOutDate, booking.objectToday)}${arrival}`,
    `${nights} ${nights === 1 ? 'Nacht' : 'Nächte'}`,
    canSeeGuests
      ? formatOccupancy(booking)
      : `${booking.guestCount} ${booking.guestCount === 1 ? 'Gast' : 'Gäste'}`,
    propertyName,
    formatBookingPrice(booking),
  ];
  return parts.filter(Boolean).join(' · ');
}

export function heroSource(booking: BookingResponse) {
  if (!isOta(booking))
    return { ...classifyChannel('Manuell'), label: 'Manuell erstellt', ota: false };
  return {
    ...classifyChannel(booking.channel ?? 'ota'),
    label: channelName(booking.channel),
    ota: true,
  };
}

const STAY_SUB: Partial<Record<NonNullable<BookingResponse['stayPhase']>, string>> = {
  Arrival: 'Anreise heute',
  InHouse: 'im Haus',
  Departure: 'Abreise heute',
  Completed: 'abgereist',
  Upcoming: 'bevorstehend',
  Pending: 'vorgemerkt',
};

/** Kurze Zeile unter jedem Schritt im Kopf (Board „Buchung“). */
export function stepSubs(
  booking: BookingResponse,
  sourceLabel: string,
  onboardingComplete: boolean | null,
  invoiceState: 'none' | 'open' | 'paid' | null,
): Record<string, string> {
  return {
    confirmed: isOta(booking) ? sourceLabel : 'manuell',
    onboarding:
      onboardingComplete === null ? '' : onboardingComplete ? 'abgeschlossen' : 'noch offen',
    stay: (booking.stayPhase && STAY_SUB[booking.stayPhase]) ?? '',
    invoice:
      invoiceState === null
        ? ''
        : invoiceState === 'paid'
          ? 'bezahlt'
          : invoiceState === 'open'
            ? 'offen'
            : 'noch keine',
  };
}
